/**
 * Project hashtag normalisation and validation.
 *
 * V1 persists project tags *with* a leading `#` (unlike observation hashtags, which are stored
 * bare - see `observation-form.utils.ts`). Comparison is always done on the bare lowercase key so
 * `#Safety` and `#safety` are the same tag, while the value sent to the API keeps the `#`.
 *
 * The backend splits both the `tags` payload and the `hashtags` filter on commas
 * (`explode(',', ...)`), so a comma inside a tag would corrupt data and is rejected.
 */

export const HASHTAG_RULES = {
  maxLength: 50,
  maxCount: 20,
  /** Letters (any script, so Arabic works), digits and underscore. */
  allowed: /^[\p{L}\p{N}_]+$/u,
};

export interface HashtagValidation {
  ok: boolean;
  /** Transloco key for the failure; empty when there was simply nothing to add. */
  messageKey?: string;
  params?: Record<string, unknown>;
}

/**
 * Bare comparable form: trim, strip leading `#`, whitespace -> `_`, lowercase.
 * Returns `''` for input that holds no tag.
 */
export function hashtagKey(raw: string | null | undefined): string {
  return String(raw ?? '')
    .trim()
    .replace(/^#+/, '')
    .trim()
    .replace(/\s+/g, '_')
    .toLowerCase();
}

/** Wire and display form V1 persists: `#` + normalised key. Returns `''` when there is no tag. */
export function toWireHashtag(raw: string | null | undefined): string {
  const key = hashtagKey(raw);
  return key ? '#' + key : '';
}

/** Normalise a list, dropping empties and case-insensitive duplicates, preserving order. */
export function dedupeHashtags(tags: readonly (string | null | undefined)[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];

  for (const tag of tags || []) {
    const key = hashtagKey(tag);
    if (!key || seen.has(key)) {
      continue;
    }
    seen.add(key);
    out.push('#' + key);
  }

  return out;
}

/** True when `tag` is already present in `existing`, compared case-insensitively. */
export function containsHashtag(
  existing: readonly (string | null | undefined)[],
  tag: string | null | undefined,
): boolean {
  const key = hashtagKey(tag);
  return !!key && (existing || []).some((item) => hashtagKey(item) === key);
}

/**
 * Validate raw user input for a single tag against `HASHTAG_RULES` and the already selected tags.
 * `messageKey` is a Transloco key; `params` carries its interpolation values.
 */
export function validateHashtag(
  raw: string | null | undefined,
  existing: readonly (string | null | undefined)[] = [],
): HashtagValidation {
  const trimmed = String(raw ?? '').trim();

  // Empty or whitespace-only input adds nothing, and is not an error worth surfacing.
  if (!trimmed || !hashtagKey(trimmed)) {
    return { ok: false, messageKey: '' };
  }

  if ((existing || []).length >= HASHTAG_RULES.maxCount) {
    return {
      ok: false,
      messageKey: 'You can add up to {{max}} hashtags',
      params: { max: HASHTAG_RULES.maxCount },
    };
  }

  const key = hashtagKey(trimmed);

  if (key.length > HASHTAG_RULES.maxLength) {
    return {
      ok: false,
      messageKey: 'Hashtag is too long (max {{max}} characters)',
      params: { max: HASHTAG_RULES.maxLength },
    };
  }

  if (!HASHTAG_RULES.allowed.test(key)) {
    return {
      ok: false,
      messageKey: 'Hashtag can only contain letters, numbers and underscores',
    };
  }

  if (containsHashtag(existing, key)) {
    return { ok: false, messageKey: 'This hashtag already exists' };
  }

  return { ok: true };
}
